import { useEffect, useState } from "react";

import RightTimeApi from "@/API";

import { FaPlusSquare } from "react-icons/fa";

import { CiEdit } from "react-icons/ci";

import { RiSave3Fill } from "react-icons/ri";

import { ImCancelCircle } from "react-icons/im";

import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";

import Enums from "@/enums";

import { toast } from "react-toastify";



type DepartmentRow = {

  id: number;

  companyId?: number;

  name: string;

  active?: boolean;

  sequence?: number;

  jobCount?: number;

  cttCount?: number;

};



export default function CompanyDepartmentsTable(props: {

  company: { id?: number };

  departments?: DepartmentRow[];

  adminPerms?: number[];

  refreshDepartments: () => Promise<unknown>;

  selectedDepartmentId?: number | null;

  onSelectDepartment?: (departmentId: number | null) => void;

  dimensionLayout?: boolean;

}) {

  const {

    company,

    departments = [],

    adminPerms,

    refreshDepartments,

    selectedDepartmentId = null,

    onSelectDepartment,

    dimensionLayout = false,

  } = props;

  const canEdit =

    adminPerms?.includes(Enums.ROLE_PERMS.EditJobs) ||

    adminPerms?.includes(Enums.ROLE_PERMS.CompanyAdmin) ||

    adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin);



  const [localDepartments, setLocalDepartments] = useState<DepartmentRow[]>([]);

  const [editingDepartment, setEditingDepartment] = useState<DepartmentRow | null>(null);



  useEffect(() => {

    setLocalDepartments(departments);

  }, [departments]);



  function startCreate() {

    if (!canEdit) return;

    const row: DepartmentRow = { id: 0, name: "", active: true, jobCount: 0, cttCount: 0 };

    setEditingDepartment(row);

    setLocalDepartments([row, ...localDepartments]);

  }



  function startEdit(e: React.MouseEvent) {

    if (!canEdit) return;

    e.stopPropagation();

    const id = parseInt(e.currentTarget.getAttribute("data-id") || "0", 10);

    const row = localDepartments.find((d) => d.id === id);

    if (row) setEditingDepartment({ ...row });

  }



  function cancelEdit() {

    if (editingDepartment?.id === 0) {

      setLocalDepartments(localDepartments.filter((d) => d.id !== 0));

    }

    setEditingDepartment(null);

  }



  function onChangeName(e: React.ChangeEvent<HTMLInputElement>) {

    if (!editingDepartment) return;

    setEditingDepartment({ ...editingDepartment, name: e.target.value });

  }



  async function saveEditAsync() {

    if (!editingDepartment || !company?.id) return;

    const name = editingDepartment.name?.trim();

    if (!name) {

      toast.error("Department name is required.");

      return;

    }



    if (editingDepartment.id === 0) {

      const resp = await RightTimeApi.postAsync("/CompanyDepartment", {

        companyId: company.id,

        name,

        sequence: editingDepartment.sequence ?? 0,

      });

      if (resp?.ok) {

        toast.success(`Department "${name}" created.`);

        setEditingDepartment(null);

        await refreshDepartments();

      }

      return;

    }



    const resp = await RightTimeApi.putAsync(`/CompanyDepartment/${editingDepartment.id}`, { name });

    if (resp?.ok) {

      toast.success(`Department "${name}" saved.`);

      setEditingDepartment(null);

      await refreshDepartments();

    }

  }



  async function toggleActiveAsync(e: React.MouseEvent) {

    if (!canEdit) return;

    e.stopPropagation();

    const id = parseInt(e.currentTarget.getAttribute("data-id") || "0", 10);

    const row = localDepartments.find((d) => d.id === id);

    if (!row || row.id <= 0) return;



    const nextActive = !(row.active ?? true);

    const resp = await RightTimeApi.putAsync(`/CompanyDepartment/${row.id}`, { active: nextActive });

    if (resp?.ok) {

      toast.success(nextActive ? `Department "${row.name}" activated.` : `Department "${row.name}" deactivated.`);

      await refreshDepartments();

    }

  }



  function handleRowClick(row: DepartmentRow) {

    if (row.id <= 0 || editingDepartment?.id === row.id) return;

    if (typeof onSelectDepartment === "function") {

      onSelectDepartment(selectedDepartmentId === row.id ? null : row.id);

    }

  }



  const wrapperClass = dimensionLayout

    ? "tableWrapper dimensionTable horizontal scrollable rowClickable companyDepartmentsTable"

    : "tableWrapper factTable horizontal scrollable companyDepartmentsTable";



  return (

    <div className={wrapperClass}>

      <table>

        <caption>

          <span className="title">Departments</span>

          <span className="captionRight">

            {canEdit ? (

              <FaPlusSquare className="actionIcon plusIcon addDepartment" onClick={startCreate} title="Add department" />

            ) : null}

          </span>

        </caption>

        <thead>

          <tr>

            <th>Id</th>

            <th className="toggleColumnHeader">Active</th>

            <th>Name</th>

            <th>Jobs</th>

            <th>Test Types</th>

            <th className="actionsHeader" />

          </tr>

        </thead>

        <tbody>

          {localDepartments.map((d) => {

            const isEditing = editingDepartment?.id === d.id;

            const isSelected = dimensionLayout && selectedDepartmentId === d.id;

            return (

              <tr

                key={`dept-${d.id}`}

                className={isSelected ? "active" : undefined}

                onClick={dimensionLayout ? () => handleRowClick(d) : undefined}

              >

                <td>{d.id > 0 ? d.id : ""}</td>

                <td

                  className="actions center"

                  data-id={d.id}

                  onClick={d.id > 0 && !isEditing ? toggleActiveAsync : undefined}

                >

                  {d.id > 0 && !isEditing ? (

                    (d.active ?? true) ? (

                      <MdCheckBox className={`checkbox checked actionIcon${canEdit ? "" : " disabled"}`} />

                    ) : (

                      <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEdit ? "" : " disabled"}`} />

                    )

                  ) : null}

                </td>

                <td>

                  {isEditing ? (

                    <input

                      className="inlineEdit nameEdit"

                      value={editingDepartment?.name ?? ""}

                      onChange={onChangeName}

                      autoFocus

                      aria-label="Department name"

                      onClick={(e) => e.stopPropagation()}

                    />

                  ) : (

                    d.name

                  )}

                </td>

                <td className="center">{d.jobCount ?? 0}</td>

                <td className="center">{d.cttCount ?? 0}</td>

                <td className="actions right">

                  {isEditing ? (

                    <>

                      <RiSave3Fill className="save actionIcon" onClick={(e) => { e.stopPropagation(); saveEditAsync(); }} title="Save" />

                      <ImCancelCircle className="cancel actionIcon" onClick={(e) => { e.stopPropagation(); cancelEdit(); }} title="Cancel" />

                    </>

                  ) : canEdit && d.id > 0 ? (

                    <CiEdit className="edit actionIcon" data-id={d.id} onClick={startEdit} title="Edit" />

                  ) : null}

                </td>

              </tr>

            );

          })}

        </tbody>

      </table>

    </div>

  );

}


